/**
 * @file DatePriceChangesDialog.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-17
 * @modified 2026-09-30
 * @description Asked after a customer order's delivery date or an invoice's date
 *              changes, when some lines price differently on the new date. Lists
 *              each line's price (and cost, where shown) now and on the new date,
 *              and what it is priced from. Tick, bottom right, takes the new
 *              prices; the small cross, bottom left, keeps the lines as they are.
 *              Driven by hooks/useDateRepricing. Also asked after an order's
 *              customer changes (changes.kind === 'customer'), for the new
 *              customer's prices.
 */

import React from 'react';
import ConfirmDialog from './ConfirmDialog';
import { formatPrice } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import './DatePriceChangesDialog.css';

const money = (v) => (v === null || v === undefined ? '—' : formatPrice(v));

function Change({ before, after }) {
  if (before === after) return <span className="dpc-same">{before}</span>;
  return (
    <span className="dpc-change">
      <span className="dpc-before">{before}</span>
      <span className="material-symbols-rounded dpc-arrow" aria-hidden="true">arrow_forward</span>
      <span className="dpc-after">{after}</span>
    </span>
  );
}

function DatePriceChangesDialog({ changes, onAccept, onKeep, includeCost = false, dateLabel = 'delivery date' }) {
  if (!changes) return null;
  const { fromDate, toDate, rows } = changes;
  const count = rows.length;
  const byCustomer = changes.kind === 'customer';

  const message = (
    <div className="dpc-body">
      {byCustomer ? (
        <p>
          The customer changed from <strong>{changes.fromName || 'the previous customer'}</strong> to{' '}
          <strong>{changes.toName || 'the new customer'}</strong>. {count === 1 ? 'This line prices' : `These ${count} lines price`} differently
          for them. Kept as they are, lines priced from the previous customer's contract lose that link when saved:
        </p>
      ) : (
        <p>
          The {dateLabel} moved from <strong>{formatDateShort(fromDate)}</strong> to{' '}
          <strong>{formatDateShort(toDate)}</strong>. {count === 1 ? 'This line prices' : `These ${count} lines price`} differently
          on the new date:
        </p>
      )}
      <div className="dpc-table-wrap">
        <table className="dpc-table">
          <thead>
            <tr>
              <th>Item</th>
              <th className="dpc-num">Sale price</th>
              {includeCost && <th className="dpc-num">Cost</th>}
              <th>Priced from</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.lineId ?? `row-${r.index}`}>
                <td>
                  {r.code && <span className="dpc-code">{r.code}</span>}
                  <span className="dpc-name">{r.name}</span>
                </td>
                <td className="dpc-num"><Change before={money(r.before.price)} after={money(r.after.price)} /></td>
                {includeCost && (
                  <td className="dpc-num"><Change before={money(r.before.cost)} after={money(r.after.cost)} /></td>
                )}
                <td>
                  {r.before.basis
                    ? <Change before={r.before.basis} after={r.after.basis} />
                    : <span className="dpc-after">{r.after.basis}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="dpc-hint">
        Tick to use the new prices. Cross to keep {count === 1 ? 'the line' : 'the lines'} as {count === 1 ? 'it is' : 'they are'}.
      </p>
    </div>
  );

  return (
    <ConfirmDialog
      isOpen
      className="date-price-changes"
      type="info"
      title={byCustomer
        ? `Prices for ${changes.toName || 'the new customer'}`
        : `Prices change on ${formatDateShort(toDate)}`}
      message={message}
      confirmText="Use the new prices"
      cancelText="Keep the current prices"
      onConfirm={onAccept}
      onCancel={onKeep}
      actionsLayout="split"
    />
  );
}

export default DatePriceChangesDialog;
